import {
  SITE_AUTH_COLOR,
  SITE_COLOR,
  SITE_EASE,
  SITE_MEDIA,
  SITE_TOUCH_TARGET,
} from "src/domains/shared/constants/styles/site";
import { paddingInlineWithSpinner } from "../Home/siteKit/SiteButtonSpinner";

const FIELD_HEIGHT = "56px";
/** 가로로 둔 터치 화면 — 화면 높이 안에 주 버튼까지 들어오게 칸을 낮춘다(누르는 높이 44px 이상) */
const FIELD_HEIGHT_LANDSCAPE = "48px";
const FIELD_RADIUS = "14px";
/** 진행 표시 고리와 버튼 글자 사이 */
const SPINNER_GAP = "8px";
const SIDE_BUTTON_PADDING_X = "20px";

/** 채운 입력 칸(포커스가 아닐 때도 테두리가 보이게) — 글자 칸·선택 칸이 같이 쓴다 */
const FIELD_INPUT = {
  display: "block",
  width: "100%",
  height: FIELD_HEIGHT,
  borderRadius: FIELD_RADIUS,
  background: SITE_AUTH_COLOR.field,
  border: `1.5px solid ${SITE_AUTH_COLOR.fieldBorder}`,
  padding: "0 18px",
  fontSize: "16px",
  color: SITE_COLOR.heading,
  fontFamily: "inherit",
  outline: "none",
  transition: "border-color .15s,box-shadow .15s,background .15s",
  appearance: "none",
  WebkitAppearance: "none",
  "&::placeholder": { color: SITE_AUTH_COLOR.placeholder },
  "&:hover": { borderColor: SITE_AUTH_COLOR.fieldBorderHover },
  [SITE_MEDIA.landscapeTouch]: { height: FIELD_HEIGHT_LANDSCAPE },
  "&:focus": {
    background: "#fff",
    borderColor: SITE_COLOR.blue,
    boxShadow: "0 0 0 4px rgba(29,116,255,.12)",
  },
  '&[aria-invalid="true"]': { borderColor: SITE_AUTH_COLOR.error },
  '&[aria-invalid="true"]:focus': {
    boxShadow: "0 0 0 4px rgba(217,48,37,.12)",
  },
} as const;

const SELECT_CHEVRON =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%238B919A' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")";

/**
 * 로그인·회원가입·비밀번호 변경 카드(marketing-auth-pages, 확정 시안 G9-6) — 「1 / 3」 단계, 제목, 안내, 입력 칸, 주 버튼, 보조 링크 줄.
 * 값은 시안 그대로다.
 */
export const AUTH_CARD_STYLES = {
  card: {
    // 카드 안 리듬: 제목 → 칸 묶음 32, 칸 묶음 → 주 버튼 28(칸이 많은 단계 22), 주 버튼 → 링크 줄 22
    '& [data-auth-part="fields"]': { marginTop: "32px" },
    '& [data-auth-part="submit"]': { marginTop: "28px" },
    '& [data-auth-part="submit"][data-tight]': { marginTop: "22px" },
    '& [data-auth-part="links"]': { marginTop: "22px" },
    animation: `authCardIn .45s ${SITE_EASE.out} both`,
    "@keyframes authCardIn": {
      from: { opacity: 0, transform: "translateY(12px)" },
      to: { opacity: 1, transform: "none" },
    },
    [SITE_MEDIA.reducedMotion]: { animation: "none" },
    // 가로로 둔 터치 화면: 머리는 왼쪽, 입력·주 버튼·링크는 오른쪽 — 세로로 쌓으면 주 버튼이 첫 화면 아래로 밀린다
    [SITE_MEDIA.landscapeTouch]: {
      display: "grid",
      gridTemplateColumns: "minmax(0,1fr) minmax(0,1.25fr)",
      columnGap: "clamp(32px,6vw,64px)",
      alignItems: "center",
      '& [data-auth-part="fields"]': { marginTop: 0 },
      '& [data-auth-part="submit"], & [data-auth-part="submit"][data-tight]': {
        marginTop: "20px",
      },
      '& [data-auth-part="links"]': { marginTop: "12px" },
    },
  },
  step: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "13px",
    fontWeight: 700,
    color: SITE_COLOR.blue,
    background: SITE_AUTH_COLOR.stepBackground,
    borderRadius: "999px",
    padding: "6px 12px",
    marginBottom: "14px",
  },
  title: {
    fontSize: "28px",
    fontWeight: 800,
    letterSpacing: "-.03em",
    lineHeight: 1.25,
    margin: 0,
    [SITE_MEDIA.phone]: { fontSize: "24px" },
  },
  description: {
    fontSize: "15px",
    color: SITE_AUTH_COLOR.description,
    lineHeight: 1.65,
    margin: "10px 0 0",
  },
  notice: { marginTop: "20px" },
  fields: {
    display: "flex",
    flexDirection: "column",
    gap: "18px",
    [SITE_MEDIA.landscapeTouch]: { gap: "14px" },
  },
  fieldsTight: { gap: "14px", [SITE_MEDIA.landscapeTouch]: { gap: "12px" } },
  field: { display: "block", minWidth: 0 },
  label: {
    display: "block",
    fontSize: "13px",
    fontWeight: 600,
    color: SITE_AUTH_COLOR.label,
    margin: "0 0 8px 2px",
    [SITE_MEDIA.landscapeTouch]: { marginBottom: "6px" },
  },
  input: FIELD_INPUT,
  select: {
    ...FIELD_INPUT,
    backgroundImage: SELECT_CHEVRON,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 16px center",
    backgroundSize: "20px",
    paddingRight: "44px",
    "&:focus": { ...FIELD_INPUT["&:focus"], backgroundImage: SELECT_CHEVRON },
    "&[data-empty]": { color: SITE_AUTH_COLOR.placeholder },
  },
  password: {
    position: "relative",
    display: "block",
    "& input": { paddingRight: "52px" },
  },
  // 보이는 단추는 28px, 누르는 영역은 44px
  eye: {
    ...SITE_TOUCH_TARGET,
    position: "absolute",
    right: "14px",
    top: "50%",
    transform: "translateY(-50%)",
    width: "28px",
    height: "28px",
    border: 0,
    background: "none",
    color: SITE_AUTH_COLOR.placeholder,
    cursor: "pointer",
    display: "grid",
    placeItems: "center",
    padding: 0,
    "& svg": { width: "22px", height: "22px" },
    "&:hover": { color: SITE_AUTH_COLOR.label },
  },
  message: {
    margin: "6px 0 0 2px",
    fontSize: "13px",
    lineHeight: 1.5,
    color: SITE_AUTH_COLOR.error,
  },
  messageSuccess: { color: SITE_AUTH_COLOR.success },
  row: { display: "flex", gap: "10px", "& > *": { flex: 1, minWidth: 0 } },
  /** 입력 칸 옆에 붙는 단추(중복확인) 줄 — 안내 문구는 이 줄 아래에 둔다 */
  inputRow: {
    display: "flex",
    gap: "10px",
    "& > input, & > span": { flex: 1, minWidth: 0 },
  },
  sideButton: {
    flex: "none",
    height: FIELD_HEIGHT,
    [SITE_MEDIA.landscapeTouch]: { height: FIELD_HEIGHT_LANDSCAPE },
    padding: `0 ${SIDE_BUTTON_PADDING_X}`,
    borderRadius: FIELD_RADIUS,
    border: `1.5px solid ${SITE_COLOR.blue}`,
    color: SITE_COLOR.blue,
    fontWeight: 700,
    fontSize: "15px",
    background: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: SPINNER_GAP,
    whiteSpace: "nowrap",
    cursor: "pointer",
    fontFamily: "inherit",
    "&:disabled": { cursor: "default" },
    // 회색 비활성 모양은 진짜 비활성(확인을 마친 아이디)에만 — 요청 중에는 파란 모양 그대로 진행 표시를 단다
    '&:disabled:not([aria-busy="true"])': {
      borderColor: SITE_AUTH_COLOR.fieldBorder,
      color: SITE_AUTH_COLOR.placeholder,
    },
    // 진행 중에도 단추 폭이 평소와 같아야 옆 입력 칸이 줄지 않는다
    '&[aria-busy="true"]': {
      cursor: "progress",
      paddingInline: paddingInlineWithSpinner(SIDE_BUTTON_PADDING_X, SPINNER_GAP),
    },
  },
  submit: {
    width: "100%",
    height: FIELD_HEIGHT,
    [SITE_MEDIA.landscapeTouch]: { height: FIELD_HEIGHT_LANDSCAPE },
    borderRadius: FIELD_RADIUS,
    border: 0,
    background: SITE_COLOR.blue,
    color: "#fff",
    fontSize: "16px",
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: SPINNER_GAP,
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "background .15s",
    "&:hover": { background: SITE_AUTH_COLOR.primaryHover },
    "&:disabled": { cursor: "default" },
    // 흐림은 진짜 비활성에만 — 요청 중에는 흐리지 않고 진행 표시를 단다
    '&:disabled:not([aria-busy="true"])': { opacity: 0.6 },
    '&[aria-busy="true"]': { cursor: "progress" },
  },
  links: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "18px",
    fontSize: "14px",
    color: SITE_AUTH_COLOR.label,
    fontWeight: 500,
    "& > i": {
      width: "1px",
      height: "12px",
      background: SITE_AUTH_COLOR.fieldBorder,
    },
    "& > button, & > a": {
      ...SITE_TOUCH_TARGET,
      border: 0,
      background: "none",
      color: "inherit",
      font: "inherit",
      cursor: "pointer",
      padding: 0,
    },
    "& > button:hover, & > a:hover": {
      color: SITE_COLOR.heading,
      textDecoration: "underline",
    },
  },
} as const;
